import type { AgentEditChromeRange } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

type IndexedRange = AgentEditChromeRange & {
	endBlockIndex: number;
	startBlockIndex: number;
};

const clampToDoc = (doc: PMNode, pos: number): number =>
	Math.min(Math.max(pos, 0), doc.content.size);

const getBlockContentRange = (doc: PMNode, blockIndex: number): AgentEditChromeRange => {
	let offset = 0;
	for (let index = 0; index < blockIndex; index++) {
		offset += doc.child(index).nodeSize;
	}

	const block = doc.child(blockIndex);
	return {
		from: offset + 1,
		to: offset + block.nodeSize - 1,
	};
};

/**
 * Expands producer positions to whole top-level blocks and coalesces positions in the same or
 * directly-adjacent blocks. An untouched top-level block splits the ranges into separate visual
 * groups; each resulting group receives its own trailing telepointer.
 */
export const normalizeAgentEditChromeRanges = (
	doc: PMNode,
	ranges: AgentEditChromeRange[],
): AgentEditChromeRange[] => {
	if (!doc.childCount) {
		return [];
	}

	const indexedRanges = ranges
		.map(({ from, to }): IndexedRange | null => {
			const start = clampToDoc(doc, Math.min(from, to));
			const end = clampToDoc(doc, Math.max(from, to));
			// `to` is exclusive. Probe one position to its left so a range ending exactly at the
			// next block boundary does not accidentally include that next block.
			const endProbe = end > start ? end - 1 : end;
			const startBlockIndex = Math.min(doc.resolve(start).index(0), doc.childCount - 1);
			const endBlockIndex = Math.min(doc.resolve(endProbe).index(0), doc.childCount - 1);

			if (startBlockIndex < 0 || endBlockIndex < 0) {
				return null;
			}

			return {
				from: start,
				to: end,
				startBlockIndex: Math.min(startBlockIndex, endBlockIndex),
				endBlockIndex: Math.max(startBlockIndex, endBlockIndex),
			};
		})
		.filter((range): range is IndexedRange => range !== null)
		.sort((a, b) => a.startBlockIndex - b.startBlockIndex || a.endBlockIndex - b.endBlockIndex);

	const groups: Array<{ endBlockIndex: number; startBlockIndex: number }> = [];
	indexedRanges.forEach(({ startBlockIndex, endBlockIndex }) => {
		const current = groups[groups.length - 1];
		if (current && startBlockIndex <= current.endBlockIndex + 1) {
			current.endBlockIndex = Math.max(current.endBlockIndex, endBlockIndex);
			return;
		}
		groups.push({ startBlockIndex, endBlockIndex });
	});

	return groups
		.map(({ startBlockIndex, endBlockIndex }) => ({
			from: getBlockContentRange(doc, startBlockIndex).from,
			to: getBlockContentRange(doc, endBlockIndex).to,
		}))
		.filter(({ from, to }) => to > from);
};
